import { QueryClient, type Query } from "@tanstack/react-query";

/**
 * MYID-D4 — TanStack Query client. Persistence layer is wired in
 * main.tsx via PersistQueryClientProvider using
 * `createSyncStoragePersister(window.localStorage)`. Queries marked
 * for persistence opt in via `meta: { persist: true }`; everything
 * else stays in-memory.
 *
 * Why selective persistence: storing ALL query data in localStorage
 * burns the 5MB quota fast (especially for paginated commerce orders).
 * Stick to the small, stable, frequently-read surfaces — notification
 * prefs, display prefs, profile demographics — where cache-first is
 * actually useful on cold start.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Predicate used by PersistQueryClientProvider's dehydrateOptions.
 * Only queries with `meta: { persist: true }` get persisted; the rest
 * stay in-memory and re-fetch on the next session.
 */
export function shouldPersistQuery(query: Query): boolean {
  return query.meta?.persist === true;
}
